// ============================================================================
// AdminSideNav — rail lateral de l'ecran Admin (AD-A, P-ADM-8, GEST-ADMIN-IA v1)
// ============================================================================
// Porte du PoC components/admin/admin-side-nav.tsx avec les TOKENS DU PARC
// (jamais les classes shadcn bg-card/text-muted-foreground du PoC). Le PoC a
// 4 sections / 8 onglets — restaure en volets groupes (GEST-ADMIN-IA). Composant
// LOCAL gest (releve Fable livrable 7 : 1er usage — promotion @gs/ui si un 2e
// ecran console le reclame). Actif = onglet courant (?tab=). SOURCE UNIQUE des
// onglets (AdminPage la consomme) — ne pas dupliquer ADMIN_TAB_KEYS ailleurs.

import { Link } from 'react-router-dom';
import {
  Bell, ClipboardList, FileText, LayoutDashboard, ScrollText, Table2, UserCheck, Users, Wallet,
  type LucideIcon,
} from 'lucide-react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { ADMIN_TAB_GROUPS, type AdminTab } from './AdminSideNav.constants';

const TAB_ICONS: Record<AdminTab, LucideIcon> = {
  overview: LayoutDashboard,
  members: Users,
  teams: Table2,
  remuneration: Wallet,
  contrats: FileText,
  questions: ClipboardList,
  reminders: Bell,
  ce: UserCheck,
  audit: ScrollText,
};

/** Lien d'onglet : ?tab= omis pour le defaut `overview` (URL canonique courte). */
function hrefFor(tab: AdminTab): string {
  return tab === 'overview' ? '/admin' : `/admin?tab=${tab}`;
}

function TabLink({ tab, active }: { tab: AdminTab; active: AdminTab }) {
  const { t } = useTranslation();
  const isActive = tab === active;
  const Icon = TAB_ICONS[tab];
  return (
    <Link
      to={hrefFor(tab)}
      aria-current={isActive ? 'page' : undefined}
      className={`flex shrink-0 touch-manipulation items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-field transition-colors ${
        isActive
          ? 'bg-brand-50 font-medium text-brand-600 dark:bg-brand-950 dark:text-brand-400'
          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200'
      }`}
    >
      <Icon size={16} aria-hidden="true" className="shrink-0" />
      <span>{t(`gest:admin.tabs.${tab}`)}</span>
    </Link>
  );
}

export function AdminSideNav({ active }: { active: AdminTab }) {
  const { t } = useTranslation();
  return (
    <nav
      aria-label={t('gest:admin.navLabel')}
      className={`${GS_COMPONENTS.card} flex gap-1 overflow-x-auto p-2 md:w-56 md:shrink-0 md:flex-col`}
    >
      <TabLink tab="overview" active={active} />

      {ADMIN_TAB_GROUPS.map(({ sectionKey, tabs }) => (
        <div
          key={sectionKey}
          className="flex flex-col gap-0.5 md:mt-2 md:border-t md:border-slate-100 md:pt-2 dark:md:border-slate-800"
        >
          <p className="hidden px-3 py-1 text-chip font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 md:block">
            {t(`gest:admin.navSections.${sectionKey}`)}
          </p>
          {tabs.map((tab) => (
            <TabLink key={tab} tab={tab} active={active} />
          ))}
        </div>
      ))}
    </nav>
  );
}
